import React, { useEffect, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { easasAlert } from "../../../fragments/easas-global-fragment/EasasGlobalAlerts";
import { useGetInstantJobData, useGetInstantJobStatus } from "api/api-hooks/instant-jobs-hooks";
import VikingMaltParametersTable, { IConstantProps, IParameterProperties } from "./parameters-table/VikingMaltParametersTable";
import VikingMaltResultsTable from "./results-table/VikingMaltResultsTable";
import params from "./parameters.json";
import taskConfig from "./taskConfig";
import { InstantJobApi } from "api/InstantJobApi";

export interface IParametersData {
    parameters: IParameterProperties;
    constants: IConstantProps;
}

interface ILogsData {
    timestamp: string;
    logType: string;
    message: string;
}

function VikingMaltBestBatchCalculator() {
    const [inputParameters, setInputParameters] = useState<Array<object>>();
    const [selectedParameters, setSelectedParameters] = useState<Array<string>>([]);
    const [parametersData, setParametersData] = useState<IParametersData>();
    const [results, setResults] = useState<any>();

    const [logs, setLogs] = useState<Array<ILogsData>>([]);
    const [filteredLogs, setFilteredLogs] = useState(logs);
    const [filterOptions, setFilterOptions] = useState<Array<object>>();
    const [logFilterValue, setLogFilterValue] = useState([]);
    const [isDialogVisible, setIsDialogVisible] = useState(false);

    const [dataLoading, setDataLoading] = useState(false);
    const [noData, setNoData] = useState(false);
    const [load, setLoad] = useState({});

    const [jobId, setJobId] = useState("");
    const [pollStatus, setPollStatus] = useState(false);
    const [dataStatus, setDataStatus] = useState(false);

    const newTaskConfig = structuredClone(taskConfig);

    if (newTaskConfig.inputGroups) {
        newTaskConfig.refTime = new Date().toISOString();
        newTaskConfig.inputGroups["real_data"][0].freestyleJson = parametersData;
    }

    const { data } = useGetInstantJobStatus(jobId, {
        enabled: pollStatus,
        refetchInterval: 2500,
        onSuccess: (data) => {
            if (data?.status === "FINISHED") {
                setPollStatus(false);
                setDataStatus(true);
            }
            if (data?.status === "FAILED") {
                setPollStatus(false);
                setDataLoading(false);
                setLoad({});
                easasAlert(`${data?.status}`, `Job Failed`, "error");
            }
            setLogs(data.logs);
        },
    });

    const { data: jobData } = useGetInstantJobData(jobId, {
        enabled: dataStatus,
        onSuccess: (data) => {
            if (!data.data.dataGroups["real_data"][0].freestyleJson["error_message"]) {
                setResults(data.data.dataGroups["real_data"][0]);
                easasAlert(`SUCCESS`, `Results Calculated`, "success");
                setDataStatus(false);
                setDataLoading(false);
                setLoad({});
            } else {
                setResults("");
                easasAlert(`FAILED`, `${data.data.dataGroups["real_data"][0].freestyleJson["error_message"]}`, "warn");
                const errorLog: ILogsData = { timestamp: new Date().toISOString(), logType: "WARN", message: data.data.dataGroups["real_data"][0].freestyleJson["error_message"] };
                setLogs((prevState) => [...prevState, errorLog]);
                setDataStatus(false);
                setDataLoading(false);
                setLoad({});
                setNoData(true);
            }
        },
        onError: () => {
            easasAlert(`FAILED`, `Results Calculation Failed`, "success");
            setDataStatus(false);
            setLoad({});
        },
    });

    useEffect(() => {
        setInputParameters(params);
        const storedSelectedParameters = sessionStorage.getItem("selectedParameters");
        if (storedSelectedParameters) {
            setSelectedParameters(JSON.parse(storedSelectedParameters));
        }
    }, []);

    useEffect(() => {
        setFilteredLogs(formatLogsDate(logs));
        const logsFilterOptionsSet = new Set();
        logs.map((log) => logsFilterOptionsSet.add(log.logType));
        const logsFilterOptions = [...logsFilterOptionsSet].map((logType) => ({ label: logType, value: logType }));
        setFilterOptions(logsFilterOptions);
    }, [logs]);

    useEffect(() => {
        let allFilteredLogs = [];
        for (let i = 0; i < logFilterValue.length; i++) {
            const logFiltered = [...logs].filter((log) => log.logType === logFilterValue[i]);
            allFilteredLogs.push(...logFiltered);
        }
        if (allFilteredLogs.length === 0) {
            setFilteredLogs(formatLogsDate(logs));
        } else {
            const allFilteredLogsSorted = [...allFilteredLogs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            setFilteredLogs(formatLogsDate(allFilteredLogsSorted));
        }
    }, [logFilterValue]);

    const multiselectChangeHandler = (e: any) => {
        sessionStorage.setItem("selectedParameters", JSON.stringify(e.value));
        setSelectedParameters(e.value);
    };

    const getResultsCalculation = async () => {
        setResults("");
        setNoData(false);
        setDataLoading(true);
        setLoad({ loading: "loading", loadingIcon: "pi pi-spin pi-sun" });
        const jobIdResponse = await InstantJobApi.enqueue(newTaskConfig);
        const jobId = jobIdResponse.data?.jobId;

        if (jobId) {
            setJobId(jobId);
            setPollStatus(true);
        } else {
            easasAlert("FAILED", `Error creating job`, "error");
            const error = jobIdResponse.error?.message;
            const errorLog: ILogsData = { timestamp: new Date().toISOString(), logType: "ERROR", message: error || "Error" };
            setLogs((prevState) => [...prevState, errorLog]);
            setDataLoading(false);
            setLoad({});
        }
    };

    const exportData = () => {
        const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(parametersData))}`;
        const link = document.createElement("a");
        link.href = jsonString;
        link.download = `Parameters-${new Date().getMinutes()}-${new Date().getSeconds()}.json`;
        link.click();
    };

    const exportLogs = () => {
        const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(logs))}`;
        const link = document.createElement("a");
        link.href = jsonString;
        link.download = `Logs-${new Date().getMinutes()}-${new Date().getSeconds()}.json`;
        link.click();
    };

    const formatLogsDate = (logsData: Array<ILogsData>) => {
        return logsData.map((log: ILogsData) => {
            const date = new Date(log.timestamp);
            const currentDate = date.toLocaleDateString("lt-LT");
            const time = date.toLocaleTimeString("lt-LT");
            return { ...log, timestamp: currentDate + " " + time };
        });
    };

    const header = (
        <div className="flex gap-2 justify-content-end align-items-center">
            <label htmlFor="logFilter">Log Type: </label>
            <MultiSelect id="logFilter" options={filterOptions} value={logFilterValue} placeholder="Select Log Type" onChange={(e) => setLogFilterValue(e.value)} />
            <Button
                icon="pi pi-file-export"
                tooltip="Export logs"
                className="p-button-success align-self-center"
                onClick={exportLogs}
                tooltipOptions={{ showDelay: 500, hideDelay: 300, position: "top" }}
            />
        </div>
    );

    return (
        <div className="flex flex-column gap-5 mb-5">
            <>
                <h2>Input Data:</h2>
                <div className="flex flex-column">
                    <div className="flex mb-3 gap-2 align-content-center">
                        <MultiSelect
                            id="parametersSelector"
                            className="col-2"
                            placeholder="Select parameters"
                            optionLabel="displayName"
                            optionValue="id"
                            options={inputParameters}
                            value={selectedParameters}
                            onChange={(e) => multiselectChangeHandler(e)}
                            filter
                        />
                        <Button
                            icon="pi pi-file-export"
                            tooltip="Export parameters data"
                            className="p-button-success align-self-center"
                            onClick={exportData}
                            tooltipOptions={{ showDelay: 500, hideDelay: 300, position: "top" }}
                        />
                        <Button label="Logs" className="align-self-center" onClick={() => setIsDialogVisible(true)} />
                        <Dialog
                            header="Logs"
                            visible={isDialogVisible}
                            style={{ width: "min(100vw, 1000px)", height: "500px" }}
                            modal={false}
                            onHide={() => {
                                setIsDialogVisible(false);
                            }}
                        >
                            <DataTable value={filteredLogs} header={header}>
                                <Column field="timestamp" header="Time stamp" />
                                <Column field="logType" header="Log Type" />
                                <Column field="message" header="Message" />
                            </DataTable>
                        </Dialog>
                    </div>

                    <div className="flex gap-2">
                        <VikingMaltParametersTable parameters={selectedParameters} setParametersData={setParametersData} setSelectedParameters={setSelectedParameters} />
                    </div>

                    <Button className="mt-5 max-w-min" label="Calculate" onClick={getResultsCalculation} {...load} />
                </div>
            </>
            {!results && dataLoading && <ProgressSpinner />}
            {results && !dataLoading && (
                <div className="mt-3">
                    <h2>Results:</h2>
                    <VikingMaltResultsTable results={results} />
                </div>
            )}
            {noData && (
                <div className="flex justify-content-center align-items-center mt-5 mb-5">
                    <h3>No Results. See logs for more info.</h3>
                </div>
            )}
        </div>
    );
}

export default VikingMaltBestBatchCalculator;
